/**
 * A leitura do comprovante de crédito pelo painel (E203.F4, ADR-0146 §7): lê os créditos lidos do
 * caso e abre o formulário (`FormularioDoCredito`). E207.F7 (owner, 01/11): na linha da conferência
 * de identidade, o formulário abre pelo resultado da linha; E207.F7.C01: com um fundamento e um
 * Salvar só. Sem crédito lido e fora da conferência, só diz que não há o que corrigir.
 */
import { useEffect, useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import { lerCreditosDoCaso, type CreditoNoPainelData } from '@/lib/revisaoDoDado';

import { FormularioDoCredito } from './FormularioDoCredito';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.credito;

function useCreditos(props: PropsDoConteudo): CreditoNoPainelData[] | null | 'erro' {
  const { client, caseId } = props;
  const [creditos, setCreditos] = useState<CreditoNoPainelData[] | null | 'erro'>(null);
  useEffect(() => {
    let vivo = true;
    lerCreditosDoCaso(client, caseId)
      .then((d) => vivo && setCreditos(d.creditos))
      .catch(() => vivo && setCreditos('erro'));
    return () => {
      vivo = false;
    };
  }, [client, caseId]);
  return creditos;
}

/** A linha da conferência de identidade que a origem leva (E207.F7), ou `null`. */
function linhaDaIdentidade(props: PropsDoConteudo): string | null {
  const linha = props.pedido.origem.linha;
  return linha !== null && linha.startsWith('identidade:') ? linha : null;
}

export function CreditoNoPainel(props: PropsDoConteudo) {
  const creditos = useCreditos(props);
  const linha = linhaDaIdentidade(props);
  if (creditos === null)
    return <p className="text-body-sm text-fg-muted">{ptBR.painelDoDado.carregando}</p>;
  if (creditos === 'erro') {
    return (
      <p role="alert" className="text-body-sm text-danger-700">
        {ptBR.painelDoDado.erro}
      </p>
    );
  }
  if (creditos.length === 0 && linha === null) {
    return (
      <p className="text-body-sm text-fg-muted" data-testid="painel-credito-sem">
        {t.semCredito}
      </p>
    );
  }
  return <FormularioDoCredito creditos={creditos} linha={linha} {...props} />;
}
